<template>
  <ValidationObserver ref="observer" tag="div" class="idtr-editor">
    <div class="idtr-scroll is-screen" ref="scroll">
      <div class="idtr-viewport" :style="viewportStyle">
        <div class="idtr-sheets" ref="sheets">
          <report-sheet
            v-for="(sheet, sheetIndex) in sheets"
            :key="sheetIndex"
            :page="sheetIndex + 1"
            :total="sheets.length"
            :title="title"
            :editable="sheetIndex === 0"
          >
            <component
              v-for="(block, i) in sheet"
              :key="block.id"
              :is="block.component"
              :ref="'block-' + block.id"
              :style="{ marginTop: (i === 0 ? 0 : block.gap) + 'px' }"
              v-bind="block.props"
              @remove="removeAccount"
            />
          </report-sheet>
        </div>
      </div>
    </div>
  </ValidationObserver>
</template>

<script>
// The form is a shared model owned by the parent page/dialog and edited in place,
// here and by the blocks (through inject).
/* eslint-disable vue/no-mutating-props */
import {ValidationObserver} from "vee-validate";
import {httpsCallable} from "firebase/functions";
import ReportSheet from "./ReportSheet.vue";
import ReportHeader from "./blocks/ReportHeader.vue";
import IntroLine from "./blocks/IntroLine.vue";
import ContactSection from "./blocks/ContactSection.vue";
import StatementSection from "./blocks/StatementSection.vue";
import AccountsSection from "./blocks/AccountsSection.vue";
import AccountBlock from "./blocks/AccountBlock.vue";
import FraudSection from "./blocks/FraudSection.vue";
import PerjuryBlock from "./blocks/PerjuryBlock.vue";
import {
  buildReportFileName,
  buildReportTitle,
  exportSheetsHtml,
  IDTR_SCALE,
  IDTR_SHEET_HEIGHT_PX,
  IDTR_SHEET_WIDTH_PX,
  paginateBlocks,
  printedAtWithDate,
  signatureDateFromPrintedAt,
} from "@/assets/utils/identity.theft.report.utils";
import {functions} from "@/assets/utils/firebase.utils";
import {getImageAsBase64} from "@/assets/utils/common.utils";
import idtrCss from "@/assets/css/identity-theft-report.raw.css";
import logoUrl from "@/assets/images/ftc-identity-theft-report-logo.png";

// Gaps between blocks in unscaled px (see identity-theft-report.raw.css)
const GAP_INTRO = 15;
const GAP_FIRST_SECTION = 15.6;
const GAP_SECTION = 45.4;
const GAP_ACCOUNT = 47.1;
const SHEET_GAP_PX = 23;

/**
 * The Identity Theft Report sheets: on-screen editing, pagination and PDF generation.
 * The form (see createForm / mergePrefill) is owned by the parent and edited in place.
 */
export default {
  components: {ReportSheet, ValidationObserver},
  props: {
    form: {type: Object, required: true},
    templateDef: {type: Object, required: true},
  },
  provide() {
    return {
      idtrForm: this.form,
      idtrTemplate: this.templateDef,
      idtrAssets: this.assets,
    };
  },
  data() {
    return {
      assets: {logo: ""},
      pages: [],
      // On-screen scale: the print scale, reduced when the content area is narrower than a sheet.
      screenScale: IDTR_SCALE,
    };
  },
  computed: {
    title() {
      return buildReportTitle(this.form.reportNumber);
    },
    fileName() {
      return buildReportFileName(this.form.reportNumber);
    },
    viewportStyle() {
      const count = this.sheets.length;
      const scale = this.screenScale;
      const height = (count * IDTR_SHEET_HEIGHT_PX + (count - 1) * SHEET_GAP_PX) * scale;
      return {
        "--idtr-screen-scale": scale,
        width: IDTR_SHEET_WIDTH_PX * scale + "px",
        height: height + "px",
      };
    },
    blocks() {
      const extraAccounts = this.form.accounts.slice(1).map((account, i) => ({
        id: `account-${i + 1}`,
        component: AccountBlock,
        gap: GAP_ACCOUNT,
        props: {index: i + 1},
      }));
      return [
        {id: "header", component: ReportHeader, gap: 0},
        {id: "intro", component: IntroLine, gap: GAP_INTRO},
        {id: "contact", component: ContactSection, gap: GAP_FIRST_SECTION},
        {id: "statement", component: StatementSection, gap: GAP_SECTION},
        {id: "accounts", component: AccountsSection, gap: GAP_SECTION},
        ...extraAccounts,
        {id: "fraud", component: FraudSection, gap: GAP_SECTION},
        {id: "perjury", component: PerjuryBlock, gap: GAP_SECTION},
      ];
    },
    sheets() {
      const byId = Object.fromEntries(
        this.blocks.map((block) => [block.id, block])
      );
      const placed = new Set();
      const sheets = this.pages
        .map((ids) =>
          ids
            .filter((id) => byId[id])
            .map((id) => {
              placed.add(id);
              return byId[id];
            })
        )
        .filter((sheet) => sheet.length);
      const leftovers = this.blocks.filter((block) => !placed.has(block.id));
      if (leftovers.length) {
        if (sheets.length) sheets[sheets.length - 1].push(...leftovers);
        else sheets.push(leftovers);
      }
      const blank = Math.max(
        0,
        Math.min(10, Number(this.form.blankPages) || 0)
      );
      for (let i = 0; i < blank; i++) sheets.push([]);
      return sheets;
    },
  },
  watch: {
    form: {deep: true, handler: "layout"},
    blocks: "layout",
    // The header date and the signature date are one day in two formats; either side can be edited.
    "form.header.printedAt"(value) {
      const next = signatureDateFromPrintedAt(value);
      if (next && next !== this.form.signatureDate) this.form.signatureDate = next;
    },
    "form.signatureDate"(value) {
      const next = printedAtWithDate(this.form.header.printedAt, value);
      if (next && next !== this.form.header.printedAt) this.form.header.printedAt = next;
    },
  },
  created() {
    if (!document.getElementById("idtr-css")) {
      const style = document.createElement("style");
      style.id = "idtr-css";
      style.textContent = idtrCss;
      document.head.appendChild(style);
    }
    getImageAsBase64(logoUrl).then((logo) => {
      this.assets.logo = logo;
      this.layout();
    });
  },
  mounted() {
    if (process.env.NODE_ENV !== "production") {
      // Dev hook: lets tooling grab the exact markup sent to the PDF function.
      window.__idtrExportHtml = () => exportSheetsHtml(this.$refs.sheets);
    }
    this.fitScale();
    this.layout();
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => this.layout());
    }
    window.addEventListener("resize", this.fitScale);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.fitScale);
  },
  methods: {
    blockElement(id) {
      const ref = this.$refs[`block-${id}`];
      const vm = Array.isArray(ref) ? ref[0] : ref;
      if (!vm) return null;
      return vm.$el || vm;
    },
    // Shrinks the sheet to the width of the content area, so it stays centered instead of
    // overflowing to the left on narrow windows or with browser zoom. Never larger than print size.
    fitScale() {
      const scroll = this.$refs.scroll;
      if (!scroll || !scroll.clientWidth) return;
      const fit = scroll.clientWidth / IDTR_SHEET_WIDTH_PX;
      const next = Math.min(IDTR_SCALE, Math.floor(fit * 10000) / 10000);
      if (next !== this.screenScale) {
        this.screenScale = next;
        this.layout();
      }
    },
    layout() {
      this.$nextTick(() => {
        const scale = this.screenScale;
        const measured = this.blocks.map((block) => {
          const el = this.blockElement(block.id);
          return {
            id: block.id,
            gap: block.gap,
            height: el ? el.getBoundingClientRect().height / scale : 0,
          };
        });
        const pages = paginateBlocks(measured);
        if (JSON.stringify(pages) !== JSON.stringify(this.pages)) {
          this.pages = pages;
        }
      });
    },
    removeAccount(index) {
      if (this.form.accounts.length > 1) this.form.accounts.splice(index, 1);
    },
    /**
     * Validates the form and prints the sheets to PDF.
     * Returns {pdf (base64), fileName}, or null when the form is invalid (a toast is shown).
     * Throws when the PDF function fails.
     */
    async generatePdf() {
      const valid = await this.$refs.observer.validate();
      if (!valid) {
        // A specific message (e.g. a future date) beats the generic "required" one.
        const specific = Object.values(this.$refs.observer.errors)
          .flat()
          .find((message) => message !== "Field is required");
        this.$toast.error(specific || "Please fill in all required fields.");
        return null;
      }
      const generate = httpsCallable(
        functions,
        "generateIdentityTheftReportPdf",
        {timeout: 180000}
      );
      const {data} = await generate({
        html: exportSheetsHtml(this.$refs.sheets),
        css: idtrCss,
        title: this.title,
        scale: IDTR_SCALE,
      });
      if (!data || !data.status) {
        throw new Error((data && data.message) || "Failed to generate PDF");
      }
      return {pdf: data.pdf, fileName: this.fileName};
    },
  },
};
</script>

<style lang="scss">
@font-face {
  font-family: "IDTR Proxima";
  font-style: normal;
  font-weight: 400;
  src: url("~@/assets/fonts/ProximaNova-Reg-webfont.woff") format("woff");
}
@font-face {
  font-family: "IDTR Arial";
  font-style: normal;
  font-weight: 400;
  src: url("~@/assets/fonts/Arimo-Regular.ttf") format("truetype");
}
.idtr-scroll {
  overflow: auto;
  padding: 16px 0 32px;
  background: #e9e9e9;
}
.idtr-viewport {
  margin: 0 auto;
}
.idtr-scroll .idtr-sheets {
  // Sheets are laid out in unscaled px and shown at A4 size (same scale as the PDF print),
  // or smaller when the content area is narrower (--idtr-screen-scale is set on .idtr-viewport,
  // which is not part of the exported markup).
  width: 1138.2px;
  transform: scale(var(--idtr-screen-scale, 0.6969));
  transform-origin: 0 0;
}
.idtr-scroll .idtr-sheet {
  margin: 0 0 23px;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.3);
}
</style>
